
import React, { useId, forwardRef } from "react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface AnimatedSelectProps {
  label: string;
  id?: string;
  options: { value: string; label: string }[];
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void; // Added for backward compatibility
  value?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export const AnimatedSelect = forwardRef<HTMLButtonElement, AnimatedSelectProps>(({
  label,
  id: providedId,
  options,
  defaultValue,
  placeholder = "Select an option",
  className,
  onChange,
  onValueChange,
  value,
  name,
  required,
  disabled,
  error,
  ...props
}, ref) => {
  const generatedId = useId();
  const id = providedId || generatedId;

  const handleValueChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    }
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  return (
    <div className="*:not-first:mt-2">
      <Label 
        htmlFor={id}
        className="text-sm font-medium mb-1 block"
      >
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <Select 
        defaultValue={defaultValue} 
        value={value} 
        onValueChange={handleValueChange}
        name={name}
        disabled={disabled}
      >
        <SelectTrigger 
          id={id} 
          ref={ref}
          className={cn(
            "bg-white border-input hover:border-[#7c3aed]/50 focus-visible:border-[#7c3aed] focus-visible:ring-[#7c3aed]/20 focus-visible:ring-[3px] transition-all duration-200",
            error && "border-red-500 focus-visible:ring-red-200",
            className
          )}
          {...props}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
});

AnimatedSelect.displayName = "AnimatedSelect";
